"use client"

import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import {
  TrendingUp,
  DollarSign,
  ShoppingBag,
  Star,
  Filter,
  RefreshCw,
  Award,
  Target,
  Activity,
  Eye,
  BarChart3
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import { useRestaurantAuth } from "../../contexts/RestaurantAuthContext"
import { useLanguage } from "../../contexts/LanguageContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import StatCard from "../../components/ui/dashboard/StatCard"
import DataCard from "../../components/ui/dashboard/DataCard"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { Badge } from "../../components/ui/badge"
import { Progress } from "../../components/ui/progress"

interface FoodItemPerformance {
  _id: string
  name: string
  category: string
  price: number
  isAvailable: boolean
  isFeatured: boolean
  isPopular: boolean
  performance: {
    totalOrders: number
    totalQuantity: number
    totalRevenue: number
    averageOrderValue: number
    averageRating: number
    ratingCount: number
  }
}

interface PerformanceData {
  period: string
  dateRange: {
    start: string
    end: string
  }
  items: FoodItemPerformance[]
}

const RestaurantFoodItemsPerformancePage: React.FC = () => {
  const { restaurantId } = useParams()
  const { user } = useRestaurantAuth()
  const { t, language, dir } = useLanguage()
  const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState("30d")
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [sortBy, setSortBy] = useState("revenue")

  const fetchPerformanceData = async () => {
    try {
      setLoading(true)
      const response = await restaurantPortalAPI.getFoodItemsPerformance(restaurantId!, selectedPeriod)
      setPerformanceData(response.data)
    } catch (error) {
      console.error("Error fetching performance data:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (restaurantId) {
      fetchPerformanceData()
    }
  }, [restaurantId, selectedPeriod])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SDG'
    }).format(amount)
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'main-dish':
        return 'bg-blue-100 text-blue-800'
      case 'appetizer':
        return 'bg-green-100 text-green-800'
      case 'dessert':
        return 'bg-purple-100 text-purple-800'
      case 'beverage':
      case 'drink':
        return 'bg-orange-100 text-orange-800'
      case 'side-dish':
        return 'bg-yellow-100 text-yellow-800'
      case 'sandwich':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getSortIcon = (field: string) => {
    if (sortBy === field) {
      return <TrendingUp className="w-4 h-4" />
    }
    return null
  }

  const handleSort = (field: string) => {
    setSortBy(field)
  }

  const filteredAndSortedItems = performanceData?.items
    .filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = selectedCategory === "all" || item.category === selectedCategory
      return matchesSearch && matchesCategory
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'revenue':
          return b.performance.totalRevenue - a.performance.totalRevenue
        case 'quantity':
          return b.performance.totalQuantity - a.performance.totalQuantity
        case 'orders':
          return b.performance.totalOrders - a.performance.totalOrders
        case 'rating':
          return (b.performance.averageRating || 0) - (a.performance.averageRating || 0)
        case 'name':
          return a.name.localeCompare(b.name)
        default:
          return 0
      }
    }) || []

  const categories = performanceData?.items
    .map(item => item.category)
    .filter((category, index, arr) => arr.indexOf(category) === index) || []

  const totalRevenue = filteredAndSortedItems.reduce((sum, item) => sum + item.performance.totalRevenue, 0)
  const totalQuantity = filteredAndSortedItems.reduce((sum, item) => sum + item.performance.totalQuantity, 0)
  const totalOrders = filteredAndSortedItems.reduce((sum, item) => sum + item.performance.totalOrders, 0)
  const avgRating = filteredAndSortedItems.length > 0
    ? filteredAndSortedItems.reduce((sum, item) => sum + (item.performance.averageRating || 0), 0) / filteredAndSortedItems.length
    : 0

  if (loading) {
    return <LoadingSpinner size="3" text={t("loadingPerformanceData")} />
  }

  return (
    <div className="space-y-6" dir={dir}>
      <PageHeader
        title={t("foodItemsPerformance")}
        subtitle={t("analyzeMenuItemsPerformance")}
      >
        <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">{t("last7Days")}</SelectItem>
              <SelectItem value="30d">{t("last30Days")}</SelectItem>
              <SelectItem value="90d">{t("last90Days")}</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchPerformanceData} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
            {t("refresh")}
          </Button>
        </div>
      </PageHeader>

      {/* Filters */}
      <DataCard
        title={t("filtersAndSearch")}
        subtitle={t("filterAndSearchFoodItemsPerformance")}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-900 dark:text-white">{t("searchItems")}</label>
            <Input
              placeholder={t("searchByName")}
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-900 dark:text-white">{t("category")}</label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allCategories")}</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>
                    {category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-900 dark:text-white">{t("sortBy")}</label>
            <Select value={sortBy} onValueChange={handleSort}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="revenue">{t("revenue")}</SelectItem>
                <SelectItem value="quantity">{t("quantitySold")}</SelectItem>
                <SelectItem value="orders">{t("numberOfOrders")}</SelectItem>
                <SelectItem value="rating">{t("averageRating")}</SelectItem>
                <SelectItem value="name">{t("name")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </DataCard>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title={t("totalRevenue")}
          value={formatCurrency(totalRevenue)}
          icon={DollarSign}
          color="bg-green-500"
          subtitle={`${t("from")} ${filteredAndSortedItems.length} ${t("items")}`}
        />
        <StatCard
          title={t("totalQuantity")}
          value={totalQuantity}
          icon={ShoppingBag}
          color="bg-blue-500"
          subtitle={t("unitsSold")}
        />
        <StatCard
          title={t("totalOrders")}
          value={totalOrders}
          icon={Target}
          color="bg-purple-500"
          subtitle={t("ordersContainingTheseItems")}
        />
        <StatCard
          title={t("avgRating")}
          value={avgRating.toFixed(1)}
          icon={Star}
          color="bg-orange-500"
          subtitle={t("averageRating")}
        />
      </div>

      {/* Performance Table */}
      <DataCard
        title={t("itemPerformanceDetails")}
        subtitle={t("detailedPerformanceMetrics")}
        isEmpty={filteredAndSortedItems.length === 0}
        emptyMessage={t("noItemsMatchFilters")}
      >
        <div className="space-y-4">
          {filteredAndSortedItems.map((item, index) => (
            <div key={item._id} className="border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-red-600 text-white rounded-full text-sm font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium text-gray-900 dark:text-white">{item.name}</h3>
                      <Badge className={getCategoryColor(item.category)}>
                        {item.category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                      {item.isFeatured && (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300">
                          {t("featured")}
                        </Badge>
                      )}
                      {item.isPopular && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-300">
                          {t("popular")}
                        </Badge>
                      )}
                      {!item.isAvailable && (
                        <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-300">
                          {t("unavailable")}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t("price")}: {formatCurrency(item.price)}
                    </p>
                  </div>
                </div>

                <div className={`grid grid-cols-2 md:grid-cols-5 gap-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(item.performance.totalRevenue)}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{t("revenue")}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{item.performance.totalQuantity}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{t("sold")}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{item.performance.totalOrders}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{t("orders")}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(item.performance.averageOrderValue)}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{t("avgOrder")}</p>
                  </div>
                  <div>
                    <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-1' : 'space-x-1'}`}>
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{(item.performance.averageRating || 0).toFixed(1)}</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      ({item.performance.ratingCount || 0} {t("reviews")})
                    </p>
                  </div>
                </div>
              </div>

              {/* Performance Bars */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-600 dark:text-gray-400">{t("revenuePerformance")}</span>
                    <span className="text-gray-900 dark:text-white">{((item.performance.totalRevenue / totalRevenue) * 100).toFixed(1)}%</span>
                  </div>
                  <Progress
                    value={(item.performance.totalRevenue / totalRevenue) * 100}
                    className="h-2"
                  />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-600 dark:text-gray-400">{t("quantityPerformance")}</span>
                    <span className="text-gray-900 dark:text-white">{((item.performance.totalQuantity / totalQuantity) * 100).toFixed(1)}%</span>
                  </div>
                  <Progress
                    value={(item.performance.totalQuantity / totalQuantity) * 100}
                    className="h-2"
                  />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-600 dark:text-gray-400">{t("rating")}</span>
                    <span className="text-gray-900 dark:text-white">{(item.performance.averageRating || 0).toFixed(1)}/5</span>
                  </div>
                  <Progress
                    value={((item.performance.averageRating || 0) / 5) * 100}
                    className="h-2"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </DataCard>
    </div>
  )
}

export default RestaurantFoodItemsPerformancePage 